<template lang="html">
  <div class="payout-capitalist-beneficiary-details">
    <template v-if="isCapitalistAccount">
      <template v-if="model">
        <div class="panel">
          <div class="panel-body">
            <div v-if="model.dirty">{{ $t('Поиск информации по счету') }} &laquo;{{ props.beneficiary }}&raquo;...
            </div>
            <div v-if="model.foundResult && model.foundResult.profile"
                 class="user-profile-details">
              <div class="user-profile-avatar"><img :src="model.foundResult.profile.avatarWeb"/></div>
              <div>{{ model.foundResult.profile.nickname }}</div>
            </div>
            <div v-if="model.foundResult && model.foundResult.accountDetails" class="account-details">
              <div class="account-details-row">
                <div>{{ model.foundResult.accountDetails.number }}</div>
                <div>{{ model.foundResult.accountDetails.name }}</div>
              </div>
              <div class="account-details-row">
                <div>{{ $t('Баланс') }}</div>
                <div>{{
                    model.foundResult.accountDetails.availBalance | moneyFormat(model.foundResult.accountDetails.currency)
                  }} {{ model.foundResult.accountDetails.currency }}
                </div>
              </div>
            </div>

            <div v-if="!model.foundResult && !model.dirty">
              <strong>{{ props.beneficiary }}:</strong> {{ $t('Не удалось загрузить данные, попробуйте позже.') }}
            </div>
          </div>
        </div>


      </template>
      <br/>
    </template>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, reactive, watch} from "vue";
import {MoneyFormatFilter} from "@/models/filters/money";
import axios from "axios";
import {WebFormModel} from "capitalist-shared/src/core/model";
import debounce from "@/polyfill/Debounce";


const validateAccount = (account?: any) => {
  if (!account || (account.trim().length == 0))
    return false;
  if (account.match(/^[WUERBHTY]\d{7,}$/))
    return true;
  return false;
}

class BeneficiaryDetailsModel extends WebFormModel {

  dirty = false
  searchingValue = ''
  results = []

  get foundResult() {
    const searchingValue = this.searchingValue ? this.searchingValue.trim() : '';
    if (this.dirty || (searchingValue.length == 0))
      return null;
    return this.getDetails(searchingValue);
  }

  get invalidData() {
    return !validateAccount(this.searchingValue);
  }

  touch(searchValue: string) {
    searchValue = searchValue ? searchValue.trim() : '';
    if (!validateAccount(searchValue)) {
      this.dirty = false;
      this.searchingValue = '';
      return;
    }
    if (this.getDetails(searchValue)) {
      this.searchingValue = searchValue;
      this.dirty = false;
      return;
    }
    this.dirty = true;
    this.searchingValue = searchValue;
    this.debouncedLoad(searchValue as string);
  }

  getDetails(beneficiary: string) {
    const idx = this.results.findIndex((r) => r.beneficiary === beneficiary)
    return idx > -1 ? this.results[idx].details : null;
  }

  update = (beneficiary: string) => {
    this.load(beneficiary).then((details) => {
      const idx = this.results.findIndex((r) => r.beneficiary === beneficiary);
      if (idx !== -1) {
        this.results.splice(idx, 1, {beneficiary, details});
      } else {
        this.results.push({beneficiary, details});
      }

      if (beneficiary === this.searchingValue) {
        this.dirty = false;
      }
    }).catch((err) => {
      if (beneficiary === this.searchingValue) {
        this.dirty = false;
      }
    });
  }

  debouncedLoad: any = debounce(this.update as any, 500)


  load(beneficiary: string) {
    return axios.post('/webapi/finance/beneficiarydetails', {
      account: beneficiary,
      call: 'detailsNorm'
    }).then((result: any) => {
      return result.data.details;
    }, (err) => {
      this.checkAxiosError(err)
      throw err;
    });
  }
}

export default defineComponent({
  name: "CapitalistInternalPaymentBeneficiaryDetails",
  filters: {
    moneyFormat: MoneyFormatFilter
  },
  props: {
    beneficiary: {default: null},
  },
  setup(props) {
    const model = reactive(new BeneficiaryDetailsModel);

    const isCapitalistAccount = computed(() => {
      return validateAccount(props.beneficiary);
    });

    const isEmpty = computed(() => {
      return !props.beneficiary || (String(props.beneficiary).trim().length == 0);
    });



    watch(() => props.beneficiary, (newVal, oldVal) => {
      model.touch(newVal as string);
    });


    return {
      isCapitalistAccount,
      isEmpty,
      props,
      model
    };
  },
});
</script>

<style scoped lang="less">
.payout-capitalist-beneficiary-details {
  width: 370px;
}
</style>

<i18n>
{
  "en": {
    "Баланс": "Balance",
    "Поиск информации по счету": "Searching for account details",
    "Не удалось загрузить данные, попробуйте позже.": "Failed to load data, try again later."
  }

}
</i18n>
